import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Golden announcement bar. Each message is its own slide (title + detail); it
// auto-advances every 5s (pausing on hover) and the left/right arrows + dots let
// visitors step through them.
const ITEMS = [
  { title: '24/7 Live Support', text: 'Got questions or need a custom quote? We’re online and ready to help.' },
  { title: 'Welcome Offer', text: 'Get 20% OFF your first order! Chat with us to claim your voucher.' },
  { title: 'Lightning-Fast Delivery', text: 'Gold and items delivered straight to you in 5–20 minutes.' },
  { title: '100% Hand-Done & Secure', text: 'Fully safe services with premium VPN protection and daily progress updates.' },
  { title: 'Trusted by Thousands', text: '7,000+ orders completed across all platforms.' },
];

const INTERVAL_MS = 5000;
const FADE_CSS = '@keyframes fsAnnounceFade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}';

export function Marquee() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + ITEMS.length) % ITEMS.length),
    [],
  );

  // Auto-advance. The effect re-runs whenever `index` changes, so manual
  // navigation also resets the 5s countdown. Pauses while hovered.
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % ITEMS.length), INTERVAL_MS);
    return () => clearTimeout(t);
  }, [index, paused]);

  const active = ITEMS[index];

  return (
    <div
      className="relative overflow-hidden bg-primary text-primary-foreground"
      role="region"
      aria-roledescription="carousel"
      aria-label="Announcements"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{FADE_CSS}</style>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="Previous announcement"
        className="absolute left-0 top-0 z-10 flex h-full items-center px-2 text-primary-foreground/70 transition-colors hover:text-primary-foreground sm:px-3"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Active slide (keyed so it re-mounts and fades on each change). */}
      <div className="mx-auto flex min-h-[2.25rem] max-w-[82%] items-center justify-center px-6 py-1.5">
        <p
          key={index}
          style={{ animation: 'fsAnnounceFade .45s ease' }}
          className="text-center text-xs leading-tight sm:text-sm"
        >
          <span className="font-bold">{active.title}</span>
          <span className="px-2 opacity-50" aria-hidden="true">|</span>
          <span className="font-medium opacity-90">{active.text}</span>
        </p>
      </div>

      <button
        type="button"
        onClick={() => go(1)}
        aria-label="Next announcement"
        className="absolute right-0 top-0 z-10 flex h-full items-center px-2 text-primary-foreground/70 transition-colors hover:text-primary-foreground sm:px-3"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 gap-1">
        {ITEMS.map((item, i) => (
          <button
            key={item.title}
            type="button"
            aria-label={`Go to announcement ${i + 1}`}
            onClick={() => setIndex(i)}
            className={`h-1 rounded-full transition-all ${
              i === index ? 'w-3 bg-primary-foreground' : 'w-1 bg-primary-foreground/40'
            }`}
          />
        ))}
      </div>

      {/* All messages in the DOM for screen readers / crawlers. */}
      <ul className="sr-only">
        {ITEMS.map((item) => (
          <li key={item.title}>
            {item.title}: {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
}
